import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import A11yDiv from '@narvar/nth-a11y-div';
import Overlay from '@narvar/nth-overlay';
import Sidebar from '@narvar/nth-sidebar';
import Spinner from '@narvar/nth-spinner';
import Icon from '../Icon';
import { Panel, PanelHeader } from '../Panel';
import s from './ReturnsSidebar.module.css';
import KEY_CODES from '../../constants/keyCodes';
import { useFocusTrap } from '../../lib/useFocusTrap';

const ReturnsSidebar = ({
  title,
  toggleSide,
  sidebarOpen,
  disableClose,
  backIconOnClick,
  closeIconOnClick,
  overlayOnClick,
  children,
  loading,
}) => {
  useEffect(() => {
    const escKeyHandler = ({ keyCode }) => {
      if (keyCode === KEY_CODES.ESCAPE && sidebarOpen && !disableClose) {
        overlayOnClick();
      }
    };
    document.addEventListener('keyup', escKeyHandler);
    return () => {
      document.removeEventListener('keyup', escKeyHandler);
    };
  }, [sidebarOpen, disableClose, overlayOnClick]);

  const [trapRef] = useFocusTrap({ enable: sidebarOpen });

  return (
    <span className="returns-sidebar-container" ref={trapRef}>
      <Sidebar open={sidebarOpen} side={toggleSide}>
        <Panel
          header={
            <PanelHeader className="sidebar-panel-header">
              {backIconOnClick ? (
                <A11yDiv
                  className="col-xs-2 text-center"
                  onClick={backIconOnClick}
                  aria-label="Back"
                  onKeyUp={({ keyCode }) => {
                    if (keyCode === KEY_CODES.ENTER || keyCode === KEY_CODES.SPACE) {
                      backIconOnClick();
                    }
                  }}
                >
                  <span className={s.iconContainer}>
                    <Icon.Chevron className="returns-sidebar-icon" direction="left" />
                  </span>
                </A11yDiv>
              ) : null}

              <div className={`col-xs-8 ${backIconOnClick ? '' : 'col-xs-offset-2'}`}>
                <p className={`returns-sidebar-title ${s.sidebar_title}`}>{title}</p>
              </div>

              {!disableClose ? (
                <A11yDiv
                  className="col-xs-2 text-center"
                  onClick={closeIconOnClick}
                  aria-label="Close"
                  id="sidebar-panel-header-close"
                  onKeyUp={({ keyCode }) => {
                    if (keyCode === KEY_CODES.ENTER || keyCode === KEY_CODES.SPACE) {
                      closeIconOnClick();
                    }
                  }}
                >
                  <span className={s.iconContainer}>
                    <Icon.Close className="returns-sidebar-icon" s={{ width: '1rem' }} />
                  </span>
                </A11yDiv>
              ) : null}
              <div className="clearfix" />
            </PanelHeader>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <Spinner style={{ height: '10rem' }} />
            </div>
          ) : (
            <div className={`${s.sidebar_content} returns-sidebar-content`}>{children}</div>
          )}
        </Panel>
      </Sidebar>
      <Overlay show={sidebarOpen} onClick={overlayOnClick} />
    </span>
  );
};

ReturnsSidebar.propTypes = {
  title: PropTypes.string.isRequired,
  toggleSide: PropTypes.oneOf(['left', 'right']).isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  disableClose: PropTypes.bool,
  backIconOnClick: PropTypes.func,
  closeIconOnClick: PropTypes.func.isRequired,
  overlayOnClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
};

ReturnsSidebar.defaultProps = {
  backIconOnClick: null,
  disableClose: false,
  loading: false,
};

export default ReturnsSidebar;
